import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import { createEsimAdminUser, listEsimAdminUsers, updateEsimAdminUser, deleteEsimAdminUser } from '../services/api';
import { UserPlus, Trash2, UserCog, KeyIcon } from 'lucide-react';
export const Settings: React.FC = () => {
  const {
    user,
    users,
    addUser,
    deleteUser,
    updateUserRole,
    changePassword
  } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'password'>('users');
  
  // ESIM Admin Users state
  const [esimAdminUsers, setEsimAdminUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  // User management states
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    status: 'Active' as 'Active' | 'Disabled',
    role: 'Support Agent' as 'Administrator' | 'Support Agent'
  });
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');
  // Password change states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Load ESIM admin users
  const loadEsimAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      const result = await listEsimAdminUsers();
      if (result && result.data) {
        setEsimAdminUsers(result.data);
      }
    } catch (error) {
      console.error('Error loading ESIM admin users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load users on component mount
  useEffect(() => {
    loadEsimAdminUsers();
  }, []);
  // Handle adding a new user
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');
    if (!newUserForm.email || !newUserForm.password) {
      setUserError('Email and password are required');
      return;
    }
    try {
      await createEsimAdminUser({
        email: newUserForm.email,
        password: newUserForm.password,
        status: newUserForm.status,
        role: newUserForm.role
      });
      setUserSuccess('User added successfully');
      setNewUserForm({
        email: '',
        password: '',
        status: 'Active',
        role: 'Support Agent'
      });
      // Reload users list
      await loadEsimAdminUsers();
    } catch (error) {
      setUserError('Failed to add user. Email may already be in use.');
    }
  };
  // Handle deleting a user
  const handleDeleteUser = async (email: string) => {
    setUserError('');
    setUserSuccess('');
    if (email === user?.email) {
      setUserError('You cannot delete your own account');
      return;
    }
    try {
      await deleteEsimAdminUser(email);
      setUserSuccess('User deleted successfully');
      await loadEsimAdminUsers();
    } catch (error) {
      setUserError('Failed to delete user');
    }
  };
  // Handle changing user role
  const handleRoleChange = async (email: string, role: 'Administrator' | 'Support Agent') => {
    setUserError('');
    setUserSuccess('');
    try {
      await updateEsimAdminUser({
        name: email,
        role: role
      });
      setUserSuccess('User role updated successfully');
      await loadEsimAdminUsers();
    } catch (error) {
      setUserError('Failed to update user role');
    }
  };
  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    const success = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    if (success) {
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      setPasswordError('Current password is incorrect');
    }
  };
  return <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage application settings
        </p>
      </div>
      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button onClick={() => setActiveTab('users')} className={`py-2 px-4 ${activeTab === 'users' ? 'border-b-2 border-alps-primary text-alps-primary' : 'text-gray-500'}`}>
          User Management
        </button>
        <button onClick={() => setActiveTab('password')} className={`py-2 px-4 ${activeTab === 'password' ? 'border-b-2 border-alps-primary text-alps-primary' : 'text-gray-500'}`}>
          Change Password
        </button>
      </div>
      {/* User Management Tab */}
      {activeTab === 'users' && <div className="space-y-6">
          <Card title="Add Support Agent">
            {userError && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{userError}</p>
                  </div>
                </div>
              </div>}
            {userSuccess && <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-green-700">{userSuccess}</p>
                  </div>
                </div>
              </div>}
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input type="email" id="email" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={newUserForm.email} onChange={e => setNewUserForm({
              ...newUserForm,
              email: e.target.value
            })} required />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input type="password" id="password" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={newUserForm.password} onChange={e => setNewUserForm({
              ...newUserForm,
              password: e.target.value
            })} required />
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select id="status" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={newUserForm.status} onChange={e => setNewUserForm({
              ...newUserForm,
              status: e.target.value as 'Active' | 'Disabled'
            })}>
                  <option value="Active">Active</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select id="role" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={newUserForm.role} onChange={e => setNewUserForm({
              ...newUserForm,
              role: e.target.value as 'Administrator' | 'Support Agent'
            })}>
                  <option value="Support Agent">Support Agent</option>
                  <option value="Administrator">Administrator</option>
                </select>
              </div>
              <div>
                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add User
                </button>
              </div>
            </form>
          </Card>
          <Card title="Manage Users">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingUsers ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                        Loading users...
                      </td>
                    </tr>
                  ) : esimAdminUsers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    esimAdminUsers.map(u => <tr key={u.name}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {u.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {u.email === user?.email ? <span className="capitalize">{u.role}</span> : <select value={u.role} onChange={e => handleRoleChange(u.email, e.target.value as 'Administrator' | 'Support Agent')} className="border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm">
                              <option value="Support Agent">Support Agent</option>
                              <option value="Administrator">Administrator</option>
                            </select>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 py-1 text-xs rounded-full ${u.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {u.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {u.email !== user?.email && <button onClick={() => handleDeleteUser(u.email)} className="text-red-600 hover:text-red-900">
                              <Trash2 className="h-5 w-5" />
                            </button>}
                        </td>
                      </tr>)
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>}
      {/* Change Password Tab */}
      {activeTab === 'password' && <Card title="Change Password">
          {passwordError && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{passwordError}</p>
                </div>
              </div>
            </div>}
          {passwordSuccess && <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-green-700">{passwordSuccess}</p>
                </div>
              </div>
            </div>}
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <input type="password" id="currentPassword" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={passwordForm.currentPassword} onChange={e => setPasswordForm({
            ...passwordForm,
            currentPassword: e.target.value
          })} required />
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input type="password" id="newPassword" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={passwordForm.newPassword} onChange={e => setPasswordForm({
            ...passwordForm,
            newPassword: e.target.value
          })} required />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <input type="password" id="confirmPassword" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({
            ...passwordForm,
            confirmPassword: e.target.value
          })} required />
            </div>
            <div>
              <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary">
                <KeyIcon className="mr-2 h-4 w-4" />
                Change Password
              </button>
            </div>
          </form>
        </Card>}
    </div>;
};